import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type ApiError } from '../api/client';
import { Alert } from '../components/Field';
import { CourseCard, type CourseCardModel } from '../components/CourseCard';
import { SiteBanners } from '../components/SiteBanners';
import { titleCase } from '../lib/format';
import { WelcomeAvatar } from '../components/WelcomeAvatar';

interface Facet {
  name: string;
  slug: string;
  count: number;
}

interface SearchResult {
  items: CourseCardModel[];
  page: number;
  totalItems: number;
  totalPages: number;
  facets: {
    categories: Facet[];
    skills: Facet[];
    difficulty: Array<{ value: string; count: number }>;
  };
}

const PRICE = [
  { value: 'free', label: 'Free' },
  { value: 'paid', label: 'Paid' },
];

const DURATION = [
  { value: 'short', label: 'Under 2 hours' },
  { value: 'medium', label: '2 – 8 hours' },
  { value: 'long', label: 'Over 8 hours' },
];

const SORTS = [
  { value: 'relevance', label: 'Most relevant' },
  { value: 'newest', label: 'Newest' },
  { value: 'popular', label: 'Most popular' },
  { value: 'rating', label: 'Highest rated' },
  { value: 'price-asc', label: 'Price: low to high' },
  { value: 'price-desc', label: 'Price: high to low' },
];

/**
 * The query keys the catalogue understands, matching CatalogSearchDto.
 *
 * The URL is shared with everything else that writes to it — campaign tags
 * (utm_source, gclid, fbclid), the ?theme= override, anything a link shortener
 * appends. The API rejects unknown fields outright, so forwarding the whole
 * query string turned every marketing link into "Request validation failed"
 * with an empty catalogue, and drew a filter chip named after the campaign.
 */
const FILTER_KEYS = [
  'q',
  'category',
  'product',
  'skill',
  'instructor',
  'difficulty',
  'price',
  'duration',
  'language',
  'minRating',
  'sort',
  'page',
  'pageSize',
] as const;

/** Only the keys above, in a stable order, so the effect does not re-run on
 *  an unrelated parameter changing. */
function filterQuery(params: URLSearchParams): string {
  const kept = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const value = params.get(key);
    if (value !== null && value !== '') kept.set(key, value);
  }
  return kept.toString();
}

/** Filters live in the URL, so a filtered view can be shared or bookmarked. */
export function Catalog() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<SearchResult | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [term, setTerm] = useState(params.get('q') ?? '');
  /** Only consulted on narrow screens; the sidebar is always open on wide ones. */
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Whether a search or filter is narrowing the list. Sort and page only
  // reorder it, so an empty result with nothing else set means an empty
  // catalog, not a search that found nothing.
  const narrowed = [...params.keys()].some((key) => key !== 'sort' && key !== 'page');

  const query = filterQuery(params);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    api<SearchResult>(`/catalog/search?${query}`)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      })
      .catch((caught) => {
        if (!cancelled) setError(caught as ApiError);
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  const setFilter = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value === null || next.get(key) === value) next.delete(key);
      else next.set(key, value);
      next.delete('page');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  // Chips describe filters, so they come from the same allowlist: a campaign
  // tag in the URL is not something the visitor chose to filter by.
  const active = [...params.entries()].filter(
    ([key]) => key !== 'page' && key !== 'sort' && (FILTER_KEYS as readonly string[]).includes(key),
  );
  // Shown on the collapsed button, so a phone can tell at a glance that
  // something is filtering the results it is looking at.
  const activeFilterCount = active.length;

  return (
    <>
      {/* The catalogue is the first SkillXzen screen most people meet, and
          they meet it before they have an account — so the assistant
          introduces herself here, and the greeting is addressed to a visitor
          rather than to an account. She shows herself, speaks, and leaves as
          the chat panel opens behind her. */}
      <WelcomeAvatar />

      <section className="cat__hero">
        <div className="wrap">
          <h1 className="cat__title">Course catalog</h1>
          <p className="cat__lede">
            Technical curriculum for engineering and IT teams — assessed, certified and measurable.
          </p>

          <form
            className="cat__searchbar"
            onSubmit={(event) => {
              event.preventDefault();
              setFilter('q', term.trim() || null);
            }}
          >
            <input
              className="field__input"
              placeholder="Search a technology, skill or instructor"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              aria-label="Search courses"
            />
            <button className="btn" type="submit">
              Search
            </button>
          </form>
        </div>
      </section>

      <div className="wrap">
        {/* The front page's scheduled banners and news, only on the plain
            catalog — not repeated on every filtered view. */}
        {active.length === 0 && (params.get('page') ?? '1') === '1' ? <SiteBanners /> : null}
        <div className="cat__layout">
          {/* On a phone the filter list is taller than the screen, and it sits
              above the results: before this, the first course was more than
              1,500px down the page — two full screens of filters before a
              single thing you could buy. So on narrow screens it collapses
              behind a button, and the courses start at the top where they
              belong. The button is hidden on wider screens, where the panel
              is a sidebar and is always open. */}
          <aside className={`filters${filtersOpen ? ' filters--open' : ''}`} aria-label="Filters">
            <button
              type="button"
              className="filters__toggle"
              aria-expanded={filtersOpen}
              aria-controls="sx-filters"
              onClick={() => setFiltersOpen((open) => !open)}
            >
              <span>Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}</span>
              <span aria-hidden="true">{filtersOpen ? '⌃' : '⌄'}</span>
            </button>

            <div className="filters__body" id="sx-filters">
            <FilterGroup legend="Category">
              {(data?.facets.categories ?? []).map((facet) => (
                <FilterOption
                  key={facet.slug}
                  label={facet.name}
                  count={facet.count}
                  on={params.get('category') === facet.slug}
                  onClick={() => setFilter('category', facet.slug)}
                />
              ))}
            </FilterGroup>

            <FilterGroup legend="Level">
              {(data?.facets.difficulty ?? []).map((facet) => (
                <FilterOption
                  key={facet.value}
                  label={titleCase(facet.value)}
                  count={facet.count}
                  on={params.get('difficulty') === facet.value}
                  onClick={() => setFilter('difficulty', facet.value)}
                />
              ))}
            </FilterGroup>

            <FilterGroup legend="Price">
              {PRICE.map((option) => (
                <FilterOption
                  key={option.value}
                  label={option.label}
                  on={params.get('price') === option.value}
                  onClick={() => setFilter('price', option.value)}
                />
              ))}
            </FilterGroup>

            <FilterGroup legend="Duration">
              {DURATION.map((option) => (
                <FilterOption
                  key={option.value}
                  label={option.label}
                  on={params.get('duration') === option.value}
                  onClick={() => setFilter('duration', option.value)}
                />
              ))}
            </FilterGroup>

            {(data?.facets.skills.length ?? 0) > 0 ? (
              <FilterGroup legend="Skill">
                {data!.facets.skills.slice(0, 10).map((facet) => (
                  <FilterOption
                    key={facet.slug}
                    label={facet.name}
                    count={facet.count}
                    on={params.get('skill') === facet.slug}
                    onClick={() => setFilter('skill', facet.slug)}
                  />
                ))}
              </FilterGroup>
            ) : null}
            </div>
          </aside>

          <section>
            {error ? <Alert kind="error" title={error.message} /> : null}

            {active.length > 0 ? (
              <div className="chips">
                {active.map(([key, value]) => (
                  <button
                    className="chip"
                    key={`${key}-${value}`}
                    onClick={() => setFilter(key, null)}
                  >
                    {key === 'q' ? `“${value}”` : titleCase(value)} ✕
                  </button>
                ))}
                <button
                  className="chip"
                  onClick={() => {
                    setTerm('');
                    setParams(new URLSearchParams(), { replace: true });
                  }}
                >
                  Clear all
                </button>
              </div>
            ) : null}

            <div className="cat__toolbar">
              <span className="cat__count">
                {data
                  ? `${data.totalItems} course${data.totalItems === 1 ? '' : 's'}`
                  : 'Searching…'}
              </span>
              <select
                className="cat__sort"
                aria-label="Sort"
                value={params.get('sort') ?? 'relevance'}
                onChange={(event) => setFilter('sort', event.target.value)}
              >
                {SORTS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {!data ? (
              <div className="spinner">Loading courses…</div>
            ) : data.items.length === 0 && !narrowed ? (
              // Nothing published at all, rather than nothing matching: "no
              // courses match those filters" with no filters set read as a
              // fault, and its Clear button did nothing.
              <div className="card">
                <div className="state">
                  <div className="state__art" aria-hidden="true">
                    ✦
                  </div>
                  <h4>New courses are coming soon</h4>
                  <p>
                    We are preparing the next set of courses. Check back shortly, or create an
                    account now so you are ready when they open.
                  </p>
                </div>
              </div>
            ) : data.items.length === 0 ? (
              <div className="card">
                <div className="state">
                  <div className="state__art" aria-hidden="true">
                    ◎
                  </div>
                  <h4>No courses match those filters</h4>
                  <p>Try removing a filter, or search for something broader.</p>
                  <button
                    className="btn btn--outline btn--sm"
                    onClick={() => {
                      setTerm('');
                      setParams(new URLSearchParams(), { replace: true });
                    }}
                  >
                    Clear all filters
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid-cards">
                  {data.items.map((course) => (
                    <CourseCard key={course.id} course={course} />
                  ))}
                </div>

                {data.totalPages > 1 ? (
                  <div className="cat__toolbar" style={{ marginTop: 22 }}>
                    <button
                      className="btn btn--ghost btn--sm"
                      disabled={data.page <= 1}
                      onClick={() => setFilter('page', String(data.page - 1))}
                    >
                      ← Previous
                    </button>
                    <span className="muted" style={{ fontSize: 13 }}>
                      Page {data.page} of {data.totalPages}
                    </span>
                    <button
                      className="btn btn--ghost btn--sm"
                      disabled={data.page >= data.totalPages}
                      onClick={() => setFilter('page', String(data.page + 1))}
                    >
                      Next →
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </section>
        </div>
      </div>
    </>
  );
}

function FilterGroup({ legend, children }: { legend: string; children: React.ReactNode }) {
  return (
    <div className="filters__group">
      <p className="filters__legend">{legend}</p>
      {children}
    </div>
  );
}

function FilterOption({
  label,
  count,
  on,
  onClick,
}: {
  label: string;
  count?: number;
  on: boolean;
  onClick: () => void;
}) {
  return (
    <button className={`filters__opt${on ? ' is-on' : ''}`} onClick={onClick} aria-pressed={on}>
      <span>
        {on ? '✓ ' : ''}
        {label}
      </span>
      {count !== undefined ? <span className="filters__count">{count}</span> : null}
    </button>
  );
}
